import React, { useEffect } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Modal from "antd/lib/modal";
import Skeleton from "antd/lib/skeleton";
import EmailThreadMessageDrawer from "./Drawers/EmailThreadMessageDrawer";
import EmptyEmailThreadMessagesIcon from "@/Assets/Others/EmailThread/EmptyEmailThreadMessagesIcon.svg";
import {
	ZomentumRoutes,
	ZomentumEntities,
	ZomentumEntityRequest
} from "@/Contracts";
import {
	EmailThreadMessage,
	DownloadEmailThreadMessageFileRequest,
	EmailThreadFile,
	EmailThreadRecipients,
	EmailThreadParticipant
} from "@/Contracts/Others/EmailThread";
import publicIp from "public-ip";
import EmailThreadActionCreators from "@/Actions/Others/EmailThread";
import ZEmailThread from "@organisms/ZEmailThread";
import { GlobalState } from "@/Reducers/interface";

export interface Props extends RouteComponentProps {
	readonly currentActiveEmailThread: EmailThreadMessage | null;
}

export const EmailThread: React.FC<Props> = props => {
	const {
		isLoading,
		userData,
		integrations,
		emailThreadMessageDrawerOpen,
		emailThreadMessages,
		currentActiveClient,
		clientContactList,
		currentActiveOpportunity
	} = useSelector((state: GlobalState) => {
		return {
			isLoading: state.emailThread.isLoading,
			userData: state.user.userData,
			integrations:
				state.user.configurationSettings.company_configs.integrations,
			emailThreadMessageDrawerOpen:
				state.emailThread.emailThreadMessageDrawerOpen,
			emailThreadMessages: state.emailThread.emailThreadMessages,
			currentActiveClient: state.clients.currentActiveClient,
			clientContactList: state.clients.clientContactList,
			currentActiveOpportunity:
				state.opportunities.currentActiveOpportunity
		};
	});
	const history = useHistory();
	const dispatch = useDispatch();
	const resetEmailThreadsDispatch = () =>
		dispatch(EmailThreadActionCreators.resetEmailThreads());
	const emailThreadMessageDrawerClosedDispatch = () =>
		dispatch(EmailThreadActionCreators.emailThreadMessageDrawerClosed());

	const showEmailThreadMessageDrawer = () => {
		if (
			userData &&
			integrations.email.findIndex(
				email => email.user_id === userData.id
			) > -1
		) {
			dispatch(
				EmailThreadActionCreators.emailThreadMessageDrawerOpened()
			);
		} else {
			Modal.confirm({
				title: "You must authenticate your email account before you can send an email",
				content:
					"If you click on OK, you will be redirected to the settings page where you can authenticate your email account",
				onOk: () => {
					history.push(ZomentumRoutes.EmailSettings);
				}
			});
		}
	};

	const closeEmailThreadMessageDrawer = () => {
		emailThreadMessageDrawerClosedDispatch();
	};

	const getEmailThreadMessages = async (
		zomentumEntityRequest: ZomentumEntityRequest
	) => {
		dispatch(EmailThreadActionCreators.showEmailThreadLoader());
		try {
			const IPv4 = await publicIp.v4();
			await dispatch(
				EmailThreadActionCreators.getEmailThreadMessages(
					zomentumEntityRequest,
					IPv4
				)
			);
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(EmailThreadActionCreators.hideEmailThreadLoader());
		}
	};

	const downloadAttachment = async (
		file: EmailThreadFile,
		messageId?: string
	) => {
		await dispatch(
			EmailThreadActionCreators.downloadEmailThreadMessageFile(
				new DownloadEmailThreadMessageFileRequest(
					file,
					props.currentActiveEmailThread?.account_id,
					messageId
				)
			)
		);
	};

	useEffect(() => {
		if (props.currentActiveEmailThread) {
			getEmailThreadMessages(
				new ZomentumEntityRequest(
					ZomentumEntities.EmailThread,
					props.currentActiveEmailThread.id,
					props.currentActiveEmailThread.account_id
				)
			);
		}
		if (emailThreadMessageDrawerOpen) {
			emailThreadMessageDrawerClosedDispatch();
		}
		return () => {
			resetEmailThreadsDispatch();
		};
	}, []);

	useEffect(() => {
		if (props.currentActiveEmailThread) {
			getEmailThreadMessages(
				new ZomentumEntityRequest(
					ZomentumEntities.EmailThread,
					props.currentActiveEmailThread.id,
					props.currentActiveEmailThread.account_id
				)
			);
		} else if (!props.currentActiveEmailThread) {
			resetEmailThreadsDispatch();
		}
	}, [props.currentActiveEmailThread]);

	const allEmailRecipients: EmailThreadRecipients[] = clientContactList
		? clientContactList.map(contact => ({
				email: contact?.email,
				name: contact?.name?.first + " " + contact?.name?.last,
				id: contact?.id
		  }))
		: new Array<EmailThreadRecipients>();

	const defaultRecipients: string[] | null = props.currentActiveEmailThread
		? props.currentActiveEmailThread.participants?.length
			? props.currentActiveEmailThread.participants.reduce(
					(final: string[], current: EmailThreadParticipant) => {
						if (
							current.email &&
							props.currentActiveEmailThread?.email_ids_to_fetch.includes(
								current.email
							) &&
							final.indexOf(current.email) === -1
						) {
							final.push(current.email);
						}
						return final;
					},
					[]
			  )
			: props.currentActiveEmailThread.from &&
			  props.currentActiveEmailThread.from.email &&
			  props.currentActiveEmailThread.from.email.length > 0
			? [props.currentActiveEmailThread.from.email]
			: null
		: null;

	const emailUserAccId = integrations.email.find(
		({ user_id }) => userData.id === user_id
	)?.account_id;

	return (
		<div className="email-thread-container">
			<EmailThreadMessageDrawer
				visible={emailThreadMessageDrawerOpen}
				onClose={closeEmailThreadMessageDrawer}
				isNewThread={false}
				isDocumentMessage={false}
				defaultRecipients={defaultRecipients}
				emailThreadMessage={props.currentActiveEmailThread}
				useTemplate={false}
				clientId={currentActiveClient?.id || null}
				opportunityId={currentActiveOpportunity?.id || null}
				allEmailRecipients={allEmailRecipients}
			/>
			<Skeleton
				loading={isLoading || !props.currentActiveEmailThread}
				avatar
				active
				paragraph={{ rows: 2 }}
				className="email-thread-messages-skeleton"
			>
				{emailThreadMessages.length === 0 && (
					<div className="drawer-form-container align-center margin-top-24px">
						<EmptyEmailThreadMessagesIcon />
						<div className="empty-component-text margin-top-24px">
							You have no emails to display in this thread
						</div>
					</div>
				)}
				<ZEmailThread
					threads={emailThreadMessages.map(emailThreadMessage => ({
						id: emailThreadMessage.id,
						from: emailThreadMessage.from,
						to: emailThreadMessage.to,
						cc: emailThreadMessage.cc,
						bcc: emailThreadMessage.bcc,
						replyTo: emailThreadMessage.reply_to,
						date: emailThreadMessage.date_time,
						body: emailThreadMessage.body,
						subject: emailThreadMessage.subject,
						snippet: emailThreadMessage.snippet,
						files: emailThreadMessage.files,
						onReply: showEmailThreadMessageDrawer,
						onDownload: downloadAttachment,
						isOthersEmailThread:
							emailThreadMessage.account_id !== emailUserAccId
					}))}
				/>
			</Skeleton>
		</div>
	);
};

export default EmailThread;
